import { db, type SyncQueue, type SyncQueuePayload } from '~/lib/pwa/db';

export class SyncQueueMaintenanceRepository {
  async add(payload: SyncQueuePayload, type: 'execute' | 'complete'): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();

    const queueItem: SyncQueue = {
      id,
      type,
      maintenanceId: 'data' in payload ? (payload.data as Record<string, string>).id : undefined,
      payload,
      status: 'pending',
      retries: 0,
      createdAt: now,
      updatedAt: now
    };

    await db.syncQueue.add(queueItem);
    return id;
  }

  async getPending(): Promise<SyncQueue[]> {
    return db.syncQueue.where('status').equals('pending').toArray();
  }

  async getErrors(): Promise<SyncQueue[]> {
    return db.syncQueue.where('status').equals('error').toArray();
  }

  async getById(id: string): Promise<SyncQueue | undefined> {
    return db.syncQueue.get(id);
  }

  async getAll(filter?: { status?: SyncQueue['status']; type?: SyncQueue['type'] }): Promise<SyncQueue[]> {
    let query = db.syncQueue.toCollection();

    if (filter?.status) {
      query = query.filter((item) => item.status === filter.status);
    }

    if (filter?.type) {
      query = query.filter((item) => item.type === filter.type);
    }

    return query.toArray();
  }

  async updateStatus(id: string, status: SyncQueue['status'], error?: string): Promise<void> {
    const updates: Partial<SyncQueue> = {
      status,
      updatedAt: Date.now()
    };

    if (error) {
      updates.error = error;
    }

    await db.syncQueue.update(id, updates);
  }

  async markAsSyncing(id: string): Promise<void> {
    await this.updateStatus(id, 'syncing');
  }

  async markAsSynced(id: string): Promise<void> {
    await this.updateStatus(id, 'synced');
  }

  async markAsError(id: string, errorMessage: string): Promise<void> {
    await this.updateStatus(id, 'error', errorMessage);
  }

  async incrementRetries(id: string): Promise<void> {
    const item = await db.syncQueue.get(id);
    if (item) {
      await db.syncQueue.update(id, {
        retries: item.retries + 1,
        updatedAt: Date.now()
      });
    }
  }

  async retryItem(id: string, maxRetries: number = 5): Promise<{ canRetry: boolean; attemptsLeft: number }> {
    const item = await db.syncQueue.get(id);
    if (!item) {
      return { canRetry: false, attemptsLeft: 0 };
    }

    const attemptsLeft = maxRetries - item.retries;

    if (item.retries >= maxRetries) {
      return { canRetry: false, attemptsLeft: 0 };
    }

    await this.updateStatus(id, 'pending');
    return { canRetry: true, attemptsLeft };
  }

  async retryErrors(maxRetries: number = 3): Promise<SyncQueue[]> {
    const errors = await this.getErrors();
    const toRetry = errors.filter((item) => item.retries < maxRetries);

    for (const item of toRetry) {
      await this.updateStatus(item.id, 'pending');
      await this.incrementRetries(item.id);
    }

    return toRetry;
  }

  async remove(id: string): Promise<void> {
    await db.syncQueue.delete(id);
  }

  async removeMany(ids: string[]): Promise<void> {
    await db.syncQueue.bulkDelete(ids);
  }

  async clearSynced(): Promise<number> {
    const synced = await db.syncQueue.where('status').equals('synced').toArray();
    await this.removeMany(synced.map((item) => item.id));
    return synced.length;
  }

  async clear(): Promise<void> {
    await db.syncQueue.clear();
  }

  async getStats(): Promise<{
    pending: number;
    syncing: number;
    synced: number;
    error: number;
    total: number;
  }> {
    const all = await this.getAll();
    return {
      pending: all.filter((i) => i.status === 'pending').length,
      syncing: all.filter((i) => i.status === 'syncing').length,
      synced: all.filter((i) => i.status === 'synced').length,
      error: all.filter((i) => i.status === 'error').length,
      total: all.length
    };
  }
}

export const syncQueueMaintenanceRepository = new SyncQueueMaintenanceRepository();
